/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { TENANTS } from "../../../constants"
import {
  DISCOUNT_PROVISION_FIXED_AMOUNT,
  DISCOUNT_PROVISION_REDUCTION_AMOUNT,
  LOCAL_STORAGE_TUNNEL_DATA_KEY,
  PROMOTION_PERCENT_TYPE,
  PROMOTION_VALUE_TYPE,
  computeServicePackPriceFromRent,
  getCountryFromLanguage,
  getDeliveryCharges,
  getFirstMainProduct,
  getNotFineancableInsurances,
  getPromotionCode,
  getPromotionDescription,
  getPromotionType,
  getPromotionValue,
  getPromotionValueType,
  isPromoAssociatedToOrder
} from "../../../core/service/BusinessLogicService"
import { translateServicePackDescriptionHtml, translateServicePackDisplayName } from "../../../core/servicePackCopyI18n"
import { loc } from "../../../core/utils"
import {
  extractAccessoriesProductNameWithAssociatedQuantity,
  extractProductNameWithAssociatedQuantity
} from "../../../core/utils.common"
import { getSessionVoyageurLang } from "../../../core/voyageurLanguage"
import { resolveStaticDocContent } from "../../../utils/staticDocumentUrls"
import CustomizedTooltip from "../../CustomizedTooltip"
import {
  ActivePromo,
  ActivePromoTypePills,
  BplsBasket,
  BplsPricingTable,
  DeliveryCharge,
  LineOfProduct,
  NotFinanceableInsurances,
  OldProductRenewal,
  PricePerMonth,
  PricingRowHT,
  PricingRowLabel,
  PricingRowTTC,
  PricingRowTVA,
  PricingTableHeaders,
  Upfront
} from "./components"
import "./styles.css"
import DOMPurify from "dompurify"
import React, { Component } from "react"
import { Col, Row, Table } from "react-bootstrap"

const getPrimaryLanguage = (languageTag) => {
  if (!languageTag || typeof languageTag !== "string") return null
  const primaryLanguage = languageTag.split("-")[0]
  return primaryLanguage ? primaryLanguage.toLowerCase() : null
}

const getTranslation = (item, languageTag) => {
  const language = getPrimaryLanguage(languageTag)
  return language ? item?.translations?.[language] : null
}

class Basket extends Component {
  state = {
    voyageurLangRevision: 0
  }

  componentDidMount() {
    window.addEventListener("voyageurLanguageChange", this.onVoyageurLanguageChange)
  }

  componentWillUnmount() {
    window.removeEventListener("voyageurLanguageChange", this.onVoyageurLanguageChange)
    document.body.style.position = ""
  }

  onVoyageurLanguageChange = () => {
    this.setState((state) => ({ voyageurLangRevision: state.voyageurLangRevision + 1 }))
  }

  formatPrice(price) {
    if (isNaN(price)) return
    return this.props.tunnelData.tenant === TENANTS.SAMSUNG_NL ? `€${price}` : `${price} €`
  }

  render() {
    const { tunnelData } = this.props
    const { tenant, displayotherInformationsBasket } = tunnelData
    const products = tunnelData?.data?.order?.products || this.props?.packContext?.product || []
    const mainProducts = products?.filter((product) => product.categoryEvollis?.mainProduct)
    const numberOfEachProductByMainProduct = extractProductNameWithAssociatedQuantity(mainProducts)

    const accessoriesProducts = products?.filter((product) => !product.categoryEvollis?.mainProduct)
    const numberOfEachProductByAccessoriesProduct =
      extractAccessoriesProductNameWithAssociatedQuantity(accessoriesProducts)

    const notFinanceableInsurances = getNotFineancableInsurances(tunnelData?.data?.order)
    const deliveryCharges = getDeliveryCharges(tunnelData?.data?.order)
    const isPromotionActiveOnOrder = isPromoAssociatedToOrder(tunnelData?.data)
    const servicePackPriceFromRent = computeServicePackPriceFromRent(tunnelData)
    const oldProductName = tunnelData?.data?.renewalOrder?.productName
    const tvaValue = tunnelData?.data?.order?.rent?.supplierVatRate
    const isBplsRetail = tenant === TENANTS.RETAIL_BPLS
    const selectedServicePack = tunnelData?.data?.order?.selectedServicePack
    const currentLanguageTag = getSessionVoyageurLang() || tunnelData?.data?.order?.orderLanguageTag || tunnelData?.lang
    const selectedServicePackTranslation = getTranslation(selectedServicePack, currentLanguageTag)
    const selectedServicePackName = selectedServicePackTranslation?.name || selectedServicePack?.name
    const selectedServicePackDescription =
      selectedServicePackTranslation?.description || selectedServicePack?.description

    const renewalProduct = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_TUNNEL_DATA_KEY)).data.order
      .renewalProduct

    return (
      <Row>
        <Col>
          <aside className="basket">
            {tenant === TENANTS.BPLS && (
              <>
                <Row>
                  <Col>
                    <p className="h1">{loc`Ma_demande_location`}</p>
                  </Col>
                </Row>

                <BplsBasket>
                  <LineOfProduct
                    mainProducts={numberOfEachProductByMainProduct}
                    accessoryProducts={numberOfEachProductByAccessoriesProduct}
                  />
                  <DeliveryCharge deliveryCharges={deliveryCharges} price={this.formatPrice(deliveryCharges)} />
                  {displayotherInformationsBasket && tunnelData?.data?.order?.rent?.monthlyTotal && (
                    <BplsPricingTable rent={tunnelData?.data?.order?.rent} />
                  )}
                </BplsBasket>

                <Row>
                  <Col>
                    <div className="soft-shadow-white-block included-services">
                      <div>
                        <p>*{loc`penalties for contract1`}</p>
                        <p>{loc`penalties for contract2`}</p>
                        <p>{loc`penalties for contract3`}</p>
                        <p>
                          <a href="http://www.orias.fr" target="_blank" rel="noopener noreferrer">
                            {loc`penalty click`}
                          </a>
                          <span>{loc`ademe number`}</span>
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}

            {tenant !== TENANTS.BPLS && (
              <>
                <Row>
                  <Col>
                    <p className="h1">{loc`Basket`}</p>
                  </Col>
                </Row>

                {!!renewalProduct && (
                  <Row>
                    <p>
                      {loc("renouvellementProduit")} : <strong>{renewalProduct}</strong>
                    </p>
                  </Row>
                )}

                <div>
                  {oldProductName && (
                    <OldProductRenewal oldProductName={oldProductName} products={getFirstMainProduct(products)} />
                  )}
                </div>

                <Row className="product d-flex align-items-center">
                  <Col style={{ paddingLeft: 0 }}>
                    <LineOfProduct
                      mainProducts={numberOfEachProductByMainProduct}
                      accessoryProducts={numberOfEachProductByAccessoriesProduct}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <ul className="order-summary list-unstyled">
                      <DeliveryCharge deliveryCharges={deliveryCharges} price={this.formatPrice(deliveryCharges)} />

                      {isPromotionActiveOnOrder && (
                        <ActivePromo
                          promo={getPromotionCode(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) || ""}
                        />
                      )}
                    </ul>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <ul className="order-summary list-unstyled">
                      {tunnelData?.data?.order?.rent?.firstCustomerPayment !== 0 && tenant !== TENANTS.RETAIL_BPLS && (
                        <Upfront
                          tunnelData={tunnelData}
                          upfrontPrice={this.formatPrice(tunnelData?.data?.order?.rent?.firstCustomerPayment || 0)}
                          oldPrice={
                            tunnelData?.data?.rentBeforeTradeIn?.firstCustomerPayment != null &&
                            tunnelData?.data?.rentBeforeTradeIn?.firstCustomerPayment !==
                              tunnelData?.data?.order?.rent?.firstCustomerPayment
                              ? this.formatPrice(tunnelData?.data?.rentBeforeTradeIn?.firstCustomerPayment)
                              : null
                          }
                        >
                          {isPromotionActiveOnOrder &&
                            getPromotionType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                              DISCOUNT_PROVISION_REDUCTION_AMOUNT &&
                            getPromotionValueType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                              PROMOTION_PERCENT_TYPE && (
                              <span className="badge badge-pill badge-primary">
                                {`- ${getPromotionValue(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) || 0} %`}
                              </span>
                            )}
                          {isPromotionActiveOnOrder &&
                            getPromotionType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                              DISCOUNT_PROVISION_REDUCTION_AMOUNT &&
                            getPromotionValueType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                              PROMOTION_VALUE_TYPE && (
                              <span className="badge badge-pill badge-primary">
                                {`- ${this.formatPrice(
                                  getPromotionValue(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) || 0
                                )}`}
                              </span>
                            )}
                          {isPromotionActiveOnOrder &&
                            tunnelData?.data?.order?.rent?.rawRentDTO?.provision &&
                            (getPromotionType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                              DISCOUNT_PROVISION_REDUCTION_AMOUNT ||
                              getPromotionType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0]) ===
                                DISCOUNT_PROVISION_FIXED_AMOUNT) && (
                              <s className="strike">
                                {this.formatPrice(tunnelData?.data?.order?.rent?.rawRentDTO?.provision || 0)}
                              </s>
                            )}
                        </Upfront>
                      )}

                      {tunnelData?.data?.order?.rent?.monthlyTotal && (
                        <li>
                          <Row className={isBplsRetail && "justify-center-row"}>
                            <PricePerMonth
                              servicePackPriceFromRentAmount={servicePackPriceFromRent}
                              servicePackPriceFromRentMessage={
                                this.formatPrice(servicePackPriceFromRent?.toFixed(2)) || this.formatPrice(0)
                              }
                              tenant={tenant}
                              duration={tunnelData?.data?.order?.rent?.duration}
                              monthlyTotal={this.formatPrice(
                                tunnelData?.data?.order?.rent?.monthlyTotal || this.formatPrice(0)
                              )}
                              oldPrice={
                                tunnelData?.data?.rentBeforeTradeIn?.monthlyRent != null &&
                                tunnelData?.data?.rentBeforeTradeIn?.monthlyRent !==
                                  tunnelData?.data?.order?.rent?.monthlyTotal
                                  ? this.formatPrice(tunnelData?.data?.rentBeforeTradeIn?.monthlyRent)
                                  : null
                              }
                            />
                          </Row>
                        </li>
                      )}

                      {displayotherInformationsBasket && (
                        <li>
                          <Row className="justify-center-row">
                            <Table borderless>
                              <PricingTableHeaders />
                              <tbody>
                                <tr>
                                  <PricingRowLabel label={loc("Upfront")} />
                                  <PricingRowHT
                                    amount={
                                      this.formatPrice(
                                        (tunnelData?.data?.order?.rent?.firstCustomerPayment * 0.8).toFixed(2)
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTVA
                                    amount={
                                      this.formatPrice(
                                        (tunnelData?.data?.order?.rent?.firstCustomerPayment * tvaValue).toFixed(2)
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTTC
                                    amount={
                                      this.formatPrice(tunnelData?.data?.order?.rent?.firstCustomerPayment) ||
                                      this.formatPrice(0)
                                    }
                                  />
                                </tr>

                                <tr>
                                  <PricingRowLabel
                                    label={
                                      getCountryFromLanguage(tunnelData?.data?.order.orderLanguageTag) === "IT"
                                        ? `${loc`Rent`} (${tunnelData?.data?.order?.rent?.duration || 0})`
                                        : `${loc`Rent`} ${loc`on`} ${
                                            tunnelData?.data?.order?.rent?.duration || 0
                                          } ${loc`months`}`
                                    }
                                  />
                                  <PricingRowHT
                                    amount={
                                      this.formatPrice(
                                        (tunnelData?.data?.order?.rent?.monthlyTotal * 0.8).toFixed(2)
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTVA
                                    amount={
                                      this.formatPrice(
                                        (tunnelData?.data?.order?.rent?.monthlyTotal * tvaValue).toFixed(2)
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTTC
                                    amount={
                                      this.formatPrice(tunnelData?.data?.order?.rent?.monthlyTotal) ||
                                      this.formatPrice(0)
                                    }
                                  />
                                </tr>

                                <tr>
                                  <PricingRowLabel label={loc("including services at")} />
                                  <PricingRowHT
                                    amount={
                                      this.formatPrice((servicePackPriceFromRent * 0.8).toFixed(2)) ||
                                      this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTVA
                                    amount={
                                      this.formatPrice((servicePackPriceFromRent * tvaValue).toFixed(2)) ||
                                      this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTTC
                                    amount={
                                      this.formatPrice(servicePackPriceFromRent?.toFixed(2)) || this.formatPrice(0)
                                    }
                                  />
                                </tr>

                                <tr>
                                  <PricingRowLabel label={loc("including insurance at")} />
                                  <PricingRowHT
                                    amount={
                                      this.formatPrice(
                                        (tunnelData?.data?.order?.rent?.monthlyUnfinancedServicesAmount * 0.8).toFixed(
                                          2
                                        )
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTVA
                                    amount={
                                      this.formatPrice(
                                        (
                                          tunnelData?.data?.order?.rent?.monthlyUnfinancedServicesAmount * tvaValue
                                        ).toFixed(2)
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                  <PricingRowTTC
                                    amount={
                                      this.formatPrice(
                                        tunnelData?.data?.order?.rent?.monthlyUnfinancedServicesAmount
                                      ) || this.formatPrice(0)
                                    }
                                  />
                                </tr>
                              </tbody>
                            </Table>
                          </Row>
                        </li>
                      )}

                      {isPromotionActiveOnOrder && (
                        <ActivePromoTypePills
                          promoType={getPromotionType(tunnelData?.data?.order?.rent?.promotionDTOs?.[0])}
                          promoCode={getPromotionCode(tunnelData?.data?.order?.rent?.promotionDTOs?.[0])}
                          promoDescription={getPromotionDescription(tunnelData?.data?.order?.rent?.promotionDTOs?.[0])}
                        />
                      )}

                      {tenant !== TENANTS.SAMSUNG_NL &&
                        tenant !== TENANTS.RETAIL_BPLS &&
                        tenant !== TENANTS.RETAIL_IT &&
                        !!notFinanceableInsurances && (
                          <li>
                            <NotFinanceableInsurances
                              notFinanceableInsuranceList={notFinanceableInsurances}
                              formattedAmount={this.formatPrice(
                                tunnelData?.data?.order?.rent?.monthlyBreakageOrThiefInsuranceLeaseAmount
                              )}
                              tenant={tenant}
                            />
                          </li>
                        )}
                    </ul>
                  </Col>
                </Row>

                {selectedServicePackName && (
                  <Row>
                    <Col>
                      <div className="soft-shadow-white-block included-services">
                        <p>
                          {getCountryFromLanguage(tunnelData?.data?.order.orderLanguageTag) === "IT"
                            ? `${loc("Summary of services included")}`
                            : translateServicePackDisplayName(selectedServicePackName, loc)}
                        </p>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              resolveStaticDocContent(
                                translateServicePackDescriptionHtml(selectedServicePackDescription, loc),
                                tunnelData
                              ),
                              {
                                ADD_ATTR: ["target"]
                              }
                            )
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                )}

                {tunnelData.dipslayProofDocuments && (
                  <Row>
                    <Col>
                      <div className="required-documents margin-top-56">
                        <Row>
                          <Col>
                            <h3>{loc`Supporting documents requested`}</h3>
                            <ul className="list-unstyled">
                              {this.props?.tunnelData.onBoardingDocuments.map((document, index) => {
                                return (
                                  <li key={document?.description + index}>
                                    <CustomizedTooltip document={document} />
                                  </li>
                                )
                              })}
                            </ul>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {this.props?.createOrderButton}
          </aside>
        </Col>
      </Row>
    )
  }
}

export default Basket
